import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaSuitcase, FaPlane, FaBed, FaCar, FaUmbrellaBeach, FaShuttleVan } from 'react-icons/fa';
import { clearFlightSearchState } from 'legacyfrontend/helpers/flightSearchStateStorage';
import { ROUTE_KEYS, SESSION_STORAGE_KEYS } from 'legacyfrontend/constants';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { useCacheConfig } from 'legacyfrontend/contexts/CacheConfigContext';
import {
  selectIsPackageSearcherEnabled,
  selectIsFlightSearcherEnabled,
  selectHasActiveSearchers,
  selectIsHotelSearcherEnabled,
} from 'legacyfrontend/redux/selectors/application-selectors';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';


const FEATURES_CONFIG = [
  {
    id: 'packages',
    label: 'Paquetes',
    icon: FaSuitcase,
    route: ROUTE_KEYS.BUSCAR_X_PAQUETES,
    enabled: true,
    active: true,
  },
  {
    id: 'flights',
    label: 'Vuelos',
    icon: FaPlane,
    route: ROUTE_KEYS.BUSCAR_X_VUELOS,
    enabled: true,
    active: true,
  },
  {
    id: 'hotels',
    label: 'Hoteles',
    icon: FaBed,
    route: ROUTE_KEYS.BUSCAR_X_HOTELES,
    externalLink: '',
    enabled: true,
    active: true,
  },
  {
    id: 'cars',
    label: 'Autos',
    icon: FaCar,
    externalLink: '',
    enabled: true,
    active: true,
  },
  {
    id: 'activities',
    label: 'Actividades',
    icon: FaUmbrellaBeach,
    externalLink: '',
    enabled: true,
    active: true,
  },
  {
    id: 'transfers',
    label: 'Traslados',
    icon: FaShuttleVan,
    externalLink: '',
    enabled: true,
    active: true,
  },
];

const FeatureButton = ({ feature, isActive, onClick }) => {
  const IconComponent = feature.icon;

  const { background } = useColors();

  if (!feature.active) {
    return <></>;
  }

  const buttonClassName = `
    w-full
    feature-selector-button group flex flex-col items-center justify-center
    ${isActive ? 'feature-selector-button--active' : 'feature-selector-button--inactive'}
    px-2 sm:px-3 transition-colors duration-200 ease-out h-12
    ${isActive ? 'bg-white shadow-sm' : 'bg-white/70 hover:bg-white hover:text-gray-800 hover:shadow-sm'}
    rounded-lg lg:backdrop-blur-3xl
    ${isActive ? 'text-gray-800' : 'text-gray-600'}
    active:bg-white/90
    focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
    ${!feature.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
  `;

  const content = (
    <div className='flex flex-row items-center justify-center'>
      <div
        className={`
          feature-selector-icon
          w-8 h-8 rounded-full flex items-center justify-center mr-3
          transition-colors duration-200 ease-out
        `}
        style={{
          border: isActive ? `2px solid ${background}` : '2px solid transparent',
        }}
      >
        <IconComponent
          size={20}
          className='text-gray-600 transition-colors duration-200 ease-out group-hover:text-gray-800'
          style={{
            color: isActive ? background : undefined,
          }}
        />
      </div>

      <span
        className={`text-sm font-medium transition-colors duration-200 ease-out ${isActive ? 'text-gray-800' : 'text-gray-600 group-hover:text-gray-800'}`}
      >
        {feature.label}
      </span>
    </div>
  );

  if (feature.externalLink) {
    return (
      <a
        href={feature.externalLink || '#'}
        onClick={(e) => {
          if (!feature.externalLink || feature.externalLink === '#') {
            e.preventDefault();
          }
          onClick(e);
        }}
        className={buttonClassName}
        style={{ '--feature-color': background }}
        target={feature.externalLink && feature.externalLink !== '#' ? '_blank' : undefined}
        rel={feature.externalLink && feature.externalLink !== '#' ? 'noopener noreferrer' : undefined}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      onClick={onClick}
      className={buttonClassName}
      style={{ '--feature-color': background }}
      disabled={!feature.enabled}
    >
      {content}
    </button>
  );
};

const FeatureSelector = () => {
  const history = useHistory();
  const location = useLocation();
  const [activeFeature, setActiveFeature] = useState(null);
  const carouselRef = useRef(null);
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const { resetOnSearchCount } = useGlobal();
  const { accionesAplicacion, isLoadingAcciones } = useCacheConfig();

  const isPackageSearcherEnabled = useSelector(selectIsPackageSearcherEnabled);
  const isFlightSearcherEnabled = useSelector(selectIsFlightSearcherEnabled);
  const isHotelSearcherEnabled = useSelector(selectIsHotelSearcherEnabled);
  const hasActiveSearchers = useSelector(selectHasActiveSearchers);

  const nameToIdMap = {
    'autos': 'cars',
    'hoteles': 'hotels',
    'actividades': 'activities',
    'traslados': 'transfers',
  };

  const enabledFeatures = useMemo(() => {
    return FEATURES_CONFIG.flatMap((feature) => {
      if (feature.externalLink !== undefined && feature.id !== 'hotels') {
        const accion = accionesAplicacion.find(
          (acc) => nameToIdMap[acc.name] === feature.id
        );

        if (accion) {
          return {
            ...feature,
            externalLink: accion.url || '',
            active: accion.activo === true,
            enabled: accion.activo === true,
          };
        }

        return {
          ...feature,
          active: false,
          enabled: false,
        };
      }

      const isPackage = feature.id === 'packages';
      const isFlight = feature.id === 'flights';
      const isHotel = feature.id === 'hotels';
      
      if (isPackage) {
        return {
          ...feature,
          active: isPackageSearcherEnabled,
          enabled: isPackageSearcherEnabled,
        };
      }
      
      if (isFlight) {
        return {
          ...feature,
          active: isFlightSearcherEnabled,
          enabled: isFlightSearcherEnabled,
        };
      }

      if (isHotel) {
        const accion = accionesAplicacion.find(
          (acc) => nameToIdMap[acc.name] === feature.id
        );

        const externalFeature =
          accion?.activo === true && accion?.url
            ? {
                ...feature,
                id: 'hotels-external',
                route: undefined,
                externalLink: accion.url,
                active: true,
                enabled: true,
              }
            : null;

        if (isHotelSearcherEnabled) {
          const internalFeature = {
            ...feature,
            externalLink: undefined,
            active: isHotelSearcherEnabled,
            enabled: isHotelSearcherEnabled,
          };

          return externalFeature ? [internalFeature, externalFeature] : [internalFeature];
        }

        return externalFeature
          ? [externalFeature]
          : [
              {
                ...feature,
                externalLink: '',
                active: false,
                enabled: false,
              },
            ];
      }
      
      return {
        ...feature,
        active: feature.active,
        enabled: feature.enabled,
      };
    });
  }, [isPackageSearcherEnabled, isFlightSearcherEnabled, isHotelSearcherEnabled, accionesAplicacion]);

  const activeFeatures = useMemo(() => {
    return enabledFeatures.filter((feature) => feature.active);
  }, [enabledFeatures]);

  const shouldRender = useMemo(() => {
    return hasActiveSearchers || activeFeatures.length > 0;
  }, [hasActiveSearchers, activeFeatures]);

  const clearAllState = useCallback(() => {
    resetOnSearchCount();

    if (isClient) {
      Object.values(SESSION_STORAGE_KEYS).forEach((key) => {
        sessionStorage.removeItem(key);
      });

      clearFlightSearchState();
      sessionStorage.removeItem('packageAdvice');
      sessionStorage.removeItem('banners');

      if ('caches' in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            if (cacheName.includes('search') || cacheName.includes('api')) {
              caches.delete(cacheName);
            }
          });
        });
      }
    }
  }, [resetOnSearchCount]);

  const handleFeatureClick = async (feature, event) => {
    if (hasDraggedRef.current) {
      event?.preventDefault();
      event?.stopPropagation();
      hasDraggedRef.current = false;
      return;
    }

    if (!feature.enabled) return;

    if (feature.externalLink !== undefined) {
      if (feature.externalLink && feature.externalLink !== '#' && feature.externalLink !== '') {
        event?.preventDefault();
        if (isClient) {
          window.open(feature.externalLink, '_blank', 'noopener,noreferrer');
        }
      } else {
        event?.preventDefault();
      }
      return;
    }

    if (event) {
      event.preventDefault();
    }

    if (activeFeature && activeFeature !== feature.id) {
      clearAllState();
    }
    setActiveFeature(feature.id);

    if (feature.route) {
      history.push(feature.route);
    }
  };

  useEffect(() => {
    const currentPath = location.pathname;
    const activeFeature = activeFeatures.find(
      (feature) => feature.enabled && feature.route && currentPath.includes(feature.route)
    );

    if (activeFeature) {
      setActiveFeature(activeFeature.id);
    } else {
      setActiveFeature(null);
    }
  }, [location.pathname, activeFeatures]);

  const hasMoreThanTwoButtons = activeFeatures.length > 2;

  const updateCarouselControls = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
    setCanScrollPrev(carousel.scrollLeft > 4);
    setCanScrollNext(carousel.scrollLeft < maxScrollLeft - 4);
  }, []);

  const handleCarouselScroll = useCallback((direction) => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    carousel.scrollBy({
      left: direction * carousel.clientWidth,
      behavior: 'smooth',
    });

    setTimeout(updateCarouselControls, 250);
  }, [updateCarouselControls]);

  const finishCarouselDrag = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel || !isDraggingRef.current) return;

    isDraggingRef.current = false;
    carousel.classList.remove('is-dragging');

    setTimeout(() => {
      hasDraggedRef.current = false;
    }, 0);
  }, []);

  const handleCarouselMouseDown = useCallback((event) => {
    const carousel = carouselRef.current;
    if (!carousel || !hasMoreThanTwoButtons) return;

    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    dragStartXRef.current = event.clientX;
    dragStartScrollLeftRef.current = carousel.scrollLeft;
    carousel.classList.add('is-dragging');
  }, [hasMoreThanTwoButtons]);

  const handleCarouselMouseMove = useCallback((event) => {
    const carousel = carouselRef.current;
    if (!carousel || !isDraggingRef.current) return;

    const dragDistance = event.clientX - dragStartXRef.current;
    if (Math.abs(dragDistance) > 4) {
      hasDraggedRef.current = true;
      event.preventDefault();
    }

    carousel.scrollLeft = dragStartScrollLeftRef.current - dragDistance;
    updateCarouselControls();
  }, [updateCarouselControls]);

  useEffect(() => {
    updateCarouselControls();
    window.addEventListener('resize', updateCarouselControls);

    return () => {
      window.removeEventListener('resize', updateCarouselControls);
    };
  }, [activeFeatures.length, updateCarouselControls]);

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      <div className='relative w-full lg:left-1/2 lg:w-max lg:max-w-[calc(100vw-2rem)] lg:-translate-x-1/2'>
        {hasMoreThanTwoButtons && canScrollPrev && (
          <>
            <div className='feature-selector-fade feature-selector-fade--left lg:hidden' aria-hidden='true' />
            <button
              type='button'
              aria-label='Ver opciones anteriores'
              className='feature-selector-arrow feature-selector-arrow--left lg:hidden'
              onClick={() => handleCarouselScroll(-1)}
            >
              <svg aria-hidden='true' viewBox='0 0 20 20' className='feature-selector-arrow__icon'>
                <path d='M12.5 4.5 7 10l5.5 5.5' fill='none' stroke='currentColor' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' />
              </svg>
            </button>
          </>
        )}

        <div 
          ref={carouselRef}
          className={`flex w-full gap-2 flex-row justify-start lg:justify-center feature-selector-container ${hasMoreThanTwoButtons ? 'flex-nowrap scrollbar-hide has-more-than-two' : 'flex-wrap'}`} 
          onScroll={updateCarouselControls}
          onMouseDown={handleCarouselMouseDown}
          onMouseMove={handleCarouselMouseMove}
          onMouseUp={finishCarouselDrag}
          onMouseLeave={finishCarouselDrag}
          style={{ 
            minHeight: '48px',
            paddingTop: '4px',
            width: '100%',
            display: 'flex',
            visibility: 'visible',
            opacity: 1,
            overflowY: hasMoreThanTwoButtons ? 'hidden' : 'visible'
          }}
        >
          {activeFeatures.map((feature) => (
            <div 
              key={feature.id} 
              className={`flex justify-center feature-button-wrapper overflow-visible`} 
              style={{ 
                visibility: 'visible',
                display: 'flex'
              }}
            >
              <FeatureButton
                feature={feature}
                isActive={activeFeature === feature.id}
                onClick={(event) => handleFeatureClick(feature, event)}
              />
            </div>
          ))}
        </div>

        {hasMoreThanTwoButtons && canScrollNext && (
          <>
            <div className='feature-selector-fade feature-selector-fade--right lg:hidden' aria-hidden='true' />
            <button
              type='button'
              aria-label='Ver más opciones'
              className='feature-selector-arrow feature-selector-arrow--right lg:hidden'
              onClick={() => handleCarouselScroll(1)}
            >
              <svg aria-hidden='true' viewBox='0 0 20 20' className='feature-selector-arrow__icon'>
                <path d='M7.5 4.5 13 10l-5.5 5.5' fill='none' stroke='currentColor' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' />
              </svg>
            </button>
          </>
        )}
      </div>
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        @media (max-width: 1023px) {
          .feature-selector-container.has-more-than-two {
            overflow-x: auto;
            overflow-y: hidden;
            scroll-snap-type: x proximity;
            overscroll-behavior-x: contain;
            touch-action: pan-x;
            -webkit-overflow-scrolling: touch;
            isolation: isolate;
            transform: translateZ(0);
            backface-visibility: hidden;
            cursor: grab;
            user-select: none;
          }

          .feature-selector-container.has-more-than-two.is-dragging {
            cursor: grabbing;
            scroll-snap-type: none;
          }

          .feature-selector-container.has-more-than-two.is-dragging * {
            user-select: none;
          }

          .feature-selector-container.has-more-than-two button,
          .feature-selector-container.has-more-than-two a {
            -webkit-tap-highlight-color: transparent;
            touch-action: pan-x;
            backface-visibility: hidden;
            will-change: auto;
          }

          .feature-selector-button {
            border: 1px solid rgba(255, 255, 255, 0.48);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.36);
          }

          .feature-selector-button--inactive {
            background: rgba(255, 255, 255, 0.38);
          }

          .feature-selector-button--inactive:hover,
          .feature-selector-button--inactive:active {
            background: rgba(255, 255, 255, 0.68);
            border-color: rgba(255, 255, 255, 0.72);
          }

          .feature-selector-button--active {
            background: rgba(255, 255, 255, 0.98);
            border-color: rgba(255, 255, 255, 0.86);
            box-shadow: 0 2px 10px rgba(15, 23, 42, 0.12);
          }

          .feature-selector-button--active .feature-selector-icon {
            background: rgba(255, 255, 255, 0.72);
          }

          @media (hover: none) and (pointer: coarse) {
            .feature-selector-container.has-more-than-two button:hover,
            .feature-selector-container.has-more-than-two a:hover,
            .feature-selector-container.has-more-than-two button:active,
            .feature-selector-container.has-more-than-two a:active {
              transform: none !important;
            }
          }
          
          .feature-selector-container.has-more-than-two .feature-button-wrapper {
            flex: 0 0 33.333%;
            width: calc((100% - 1rem) / 3);
            flex-shrink: 0;
            scroll-snap-align: start;
            overflow: hidden;
            contain: layout paint;
            transform: translateZ(0);
            backface-visibility: hidden;
          }

          @media (min-width: 640px) {
            .feature-selector-container.has-more-than-two .feature-button-wrapper {
              flex-basis: calc((100% - 1.5rem) / 4);
              width: calc((100% - 1.5rem) / 4);
            }
          }

          @media (min-width: 768px) {
            .feature-selector-container.has-more-than-two .feature-button-wrapper {
              flex-basis: calc((100% - 2rem) / 5);
              width: calc((100% - 2rem) / 5);
            }
          }

          .feature-selector-arrow {
            position: absolute;
            top: 50%;
            z-index: 3;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 30px;
            height: 30px;
            border: 1px solid rgba(255, 255, 255, 0.72);
            border-radius: 9999px;
            background: rgba(255, 255, 255, 0.78);
            box-shadow: 0 2px 6px rgba(15, 23, 42, 0.12);
            color: #374151;
            cursor: pointer;
            transform: translateY(-50%);
            backdrop-filter: blur(8px);
            transition: background-color 200ms ease-out, border-color 200ms ease-out, color 200ms ease-out, box-shadow 200ms ease-out;
          }

          .feature-selector-arrow__icon {
            width: 16px;
            height: 16px;
            pointer-events: none;
          }

          .feature-selector-arrow:hover,
          .feature-selector-arrow:active {
            background: rgba(255, 255, 255, 0.94);
            border-color: rgba(255, 255, 255, 0.9);
            color: #111827;
            box-shadow: 0 3px 8px rgba(15, 23, 42, 0.16);
            transform: translateY(-50%);
          }

          .feature-selector-arrow:focus {
            outline: none;
          }

          .feature-selector-arrow:focus-visible {
            outline: 2px solid rgba(255, 255, 255, 0.82);
            outline-offset: 2px;
          }

          .feature-selector-fade {
            position: absolute;
            top: 0;
            bottom: 0;
            z-index: 2;
            width: 42px;
            pointer-events: none;
          }

          .feature-selector-fade--left {
            left: 0;
            background: linear-gradient(90deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0));
          }

          .feature-selector-fade--right {
            right: 0;
            background: linear-gradient(270deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0));
          }

          .feature-selector-arrow--left {
            left: 2px;
          }

          .feature-selector-arrow--right {
            right: 2px;
          }
        }
        
        @media (min-width: 1024px) {
          .feature-selector-container.has-more-than-two {
            padding-left: 0;
            padding-right: 0;
            overflow-x: visible;
            scroll-snap-type: none;
            width: max-content;
            max-width: calc(100vw - 2rem);
          }
          
          .feature-selector-container.has-more-than-two .feature-button-wrapper {
            flex: 0 0 auto;
            width: 140px;
            min-width: 130px;
            max-width: 140px;
            scroll-snap-align: none;
          }
        }
      `}</style>
    </>
  );
};

export default FeatureSelector;
